import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Check } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { setErr("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || "Something went wrong";
      if (msg.toLowerCase().includes("invalid") || msg.toLowerCase().includes("expired")) setInvalid(true);
      else setErr(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="reset-password-screen">
        {!done && (
          <div className="mb-6 flex flex-col items-center text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white"><KeyRound className="h-6 w-6" /></div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Choose a new password</h1>
          </div>
        )}
        {invalid ? (
          <div className="space-y-4 text-center">
            <p data-testid="reset-invalid" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">This reset link is invalid or has expired. Please request a new one.</p>
            <Link to="/forgot-password" className="inline-block font-semibold text-primary">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="flex flex-col items-center space-y-3 text-center" data-testid="reset-success">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100"><Check className="h-7 w-7 text-emerald-600" strokeWidth={3} /></div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Password reset successfully</h1>
            <p className="text-sm text-slate-500">You can now log in with your new password.</p>
            <Link to="/login" data-testid="reset-login-btn" className="w-full pt-2"><Button className="w-full rounded-full font-semibold text-white hover:opacity-90" style={{ backgroundColor: "#1E40AF" }}>Log in</Button></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="pw">New password</Label>
              <Input id="pw" data-testid="reset-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required className="mt-1 rounded-lg" placeholder="••••••••" />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm password</Label>
              <Input id="confirm" data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1 rounded-lg" placeholder="••••••••" />
            </div>
            {err && <p data-testid="reset-error" className="text-xs text-red-500">{err}</p>}
            <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full rounded-full bg-primary font-semibold hover:bg-primary/90">
              {loading ? "Resetting…" : "Reset password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
